import axios from 'axios';
import algoliaRecommend, { RecommendClient } from '@algolia/recommend';
import {
  ALGOLIA_RECOMMEND_THRESHOLD,
  ALGOLIA_APP,
  ALGOLIA_INDEX,
  ALGOLIA_SEARCH_KEY,
  SEARCH_ENGINE
} from '@constants';
import { API_HOST_PROXY } from '@constants/api';
import { handleServiceError } from '@utils';
import { ProductDiscount, ProductDetails, ProductHit, Product, ProductsResponse, ReviewSubmitPayload } from './interfaces';
import { mapAlgoliaProduct } from './mapper';
import { AuthorizationService } from '../auth';
import { SearchEngineBFFClient } from '../search/bffClient';

export class ProductService extends AuthorizationService {
  static readonly serviceName: string = 'ProductService';
  private algoliaRecommend: RecommendClient;
  private searchClient: SearchEngineBFFClient;

  constructor() {
    super();
    this.algoliaRecommend = algoliaRecommend(ALGOLIA_APP, ALGOLIA_SEARCH_KEY);
    this.searchClient = new SearchEngineBFFClient(SEARCH_ENGINE);
  }

  public async getProductData(id: string, ssr?: boolean): Promise<ProductDetails | null> {
    try {
      const { data } = await axios.get(`${API_HOST_PROXY}/products/${id}/details`,
        ssr && { params: { ssr } }
      );

      return data;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getProductDataByIds(ids: string[]): Promise<Product[] | null> {
    try {
      const { data } = await axios.get(`${API_HOST_PROXY}/products/previews`, {
        params: { ids: ids.join(',') }
      });

      return data;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getActiveProductDiscounts(): Promise<ProductDiscount[] | null> {
    try {
      const validUntil = new Date().toISOString();

      const { data } = await axios.get(`${API_HOST_PROXY}/products/discounts?validUntil=${validUntil}&page=0&size=100`);

      return data?.results ?? null;
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }

  /**
   * TODO: Based on search engine configuration, should go to MXP for recommendations
   * TODO: keep these in product service until we have recommendations service
   * @param categoryId
   */
  public async getRecommendedProductsForCategory(categoryId:string): Promise<ProductsResponse | null> {
    try {
      const res = await this.algoliaRecommend.getTrendingItems([ {
        indexName: ALGOLIA_INDEX,
        threshold: ALGOLIA_RECOMMEND_THRESHOLD,
        facetName: 'categories.id',
        facetValue: categoryId
      } ]);
      const hits = res.results[0].hits as unknown as ProductHit[];

      return {
        offset: res.results[0].page,
        count: res.results[0].hits.length,
        total: res.results[0].nbHits,
        results: hits.map(mapAlgoliaProduct)
      };
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getRelatedProductsAlgoliaAWS(
    productId: string,
    variantId: string,
    categoryId?: string
  ): Promise<ProductsResponse | null> {
    try {
      const res = await this.algoliaRecommend.getRelatedProducts([ {
        indexName: ALGOLIA_INDEX,
        threshold: ALGOLIA_RECOMMEND_THRESHOLD,
        maxRecommendations: 8,
        objectID: `${productId}-${variantId}`,
        fallbackParameters: categoryId ? {
          facetFilters: [ `categories.id:${categoryId}`, `id:-${productId}`, 'isOnStock:true' ]
        } : null
      } ]);
      const hits = res.results[0].hits as unknown as ProductHit[];

      return {
        offset: res.results[0].page,
        count: res.results[0].hits.length,
        total: res.results[0].nbHits,
        results: hits.map(mapAlgoliaProduct)
      };
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getRelatedProducts(
    productId: string,
    variantId: string,
    categoryTree: string,
    selectedTenantKey?: string
  ): Promise<ProductsResponse | null> {
    try {
      const res = await this.searchClient.getRelatedItems({
        productId,
        variantId,
        categoryTree,
        selectedTenantKey
      });

      return {
        offset: res.offset,
        count: res.count,
        total: res.total,
        results: res.results
      };
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getProductRecommendations(
    categoryTree: string
  ): Promise<Product[] | null> {
    try {
      const res = await this.searchClient.getProductRecommendations({ categoryTree });

      return res.products;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getProductRecommendationsAWS(
    categoryId: string
  ): Promise<Product[] | null> {
    try {
      const res = await this.searchClient.getProductRecommendationsAWS({ categoryId });

      return res.products;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getAvailableQuantity(sku: string): Promise<number | null> {
    try {
      if (!sku) return null;

      const { data } = await axios.get(`${API_HOST_PROXY}/inventories/${sku}`);

      return data?.availableQuantity ?? null;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async submitReview(id: string, payload: ReviewSubmitPayload): Promise<boolean> {
    try {
      await axios.post(`${API_HOST_PROXY}/products/${id}/reviews`, payload);

      return true;
    } catch (err) {
      handleServiceError(err);

      return false;
    }
  }
}

